import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { normalizeDateFormat, normalizeTimeFormat } from '../i18n/date-format';
import { ProjectInfo } from '../models/project-info';
import {
  DEFAULT_PROJECT_GRID_VIEW,
  ProjectGridModel,
  ProjectGridView,
  StatusFilter,
} from '../models/project-grid.types';
import {
  EMPTY_CRITERIA,
  availableColumns,
  buildProjectGridModel,
  mergeUnknownColumns,
  searchHaystack,
} from '../services/project-grid-view';
import {
  MAX_GROUP_LEVELS,
  moveColumn,
  REQUIRED_COLUMN,
  setFilter,
  toggleColumn,
  toggleGroupBy,
  toggleSort,
} from '../services/project-grid-actions';
import { ProjectGridColumnDef, ProjectGridFilter } from '../models/project-grid.types';
import { destinationAfterSelection } from '../services/project-selection-gate';
import { consumeIntendedUrl, peekIntendedUrl } from '../auth/return-to';
import { buildProjectLink } from '../services/project-deep-link';
import { ProjectListLoad, ProjectService } from '../services/project.service';
import { PreferencesService } from '../services/preferences.service';
import { PreferenceTier, SavedProjectGridView } from '../services/preferences.types';
import {
  canResetTier,
  DEFAULT_SAVE_TIER,
  resetLabel,
  saveTargets,
  TierOption,
} from '../services/project-grid-save';
import { UploadDialogService } from '../services/upload-dialog.service';
import { UserAuthService } from '../UserAuth.service';

/**
 * Project selection, as a SCREEN.
 *
 * It used to be a modal floating over whichever cure screen happened to be behind
 * it, with its own minimize, maximize, drag and eight resize handles — all of which
 * are gone with it. Joel: the project-dependent tabs "should not be available until
 * a project is selected", which makes selection the first thing a session does, and
 * a dialog you must dismiss to see what you chose was always the wrong shape for
 * that.
 *
 * The grid model is computed HERE, once per data or criteria change, and handed to
 * a presentational grid. See project-grid-view for why that matters more than any
 * grid library would have.
 */
@Component({
  selector: 'app-project-selection',
  templateUrl: './project-selection.component.html',
  styleUrls: ['./project-selection.component.scss'],
})
export class ProjectSelectionComponent implements OnInit, OnDestroy {
  searchTerm = '';
  selectedFilter: StatusFilter = 'all';
  showAdvancedFilters = false;
  dateFilterFrom = '';
  dateFilterTo = '';

  gridModel: ProjectGridModel = buildProjectGridModel([], DEFAULT_PROJECT_GRID_VIEW, EMPTY_CRITERIA);

  /**
   * The resolved saved view. Server-normalised — the three tiers are merged and made
   * coherent in DataServices, so there is one implementation of the cascade and this
   * is not it. Until preferences load, the built-in default stands.
   */
  gridView: ProjectGridView = DEFAULT_PROJECT_GRID_VIEW;

  projectLoad: ProjectListLoad = { kind: 'idle' };
  shareLinkCopiedFor: string | null = null;

  /** Where to go once a project is chosen, when the user was sent here by the gate. */
  returnTo: string | null = null;

  /** Every column this caller could choose — built-ins plus one per user-defined field. */
  columnCatalogue: ProjectGridColumnDef[] = [];

  /** Is the column chooser open? Closed by default: the grid is the point of the screen. */
  showColumnChooser = false;

  readonly requiredColumn = REQUIRED_COLUMN;
  readonly maxGroupLevels = MAX_GROUP_LEVELS;

  /** Which tiers this user may save to. Server-enforced; this is what we OFFER. */
  saveTargets: TierOption[] = [];

  /** Transient confirmation after a save or reset — never a silent success. */
  saveStatus: string | null = null;

  private companyName: string | null = null;
  private companyLocked = false;

  private haystacks = new Map<ProjectInfo, string>();
  private readonly destroying$ = new Subject<void>();

  constructor(
    public projectService: ProjectService,
    private authService: UserAuthService,
    private preferences: PreferencesService,
    private uploadDialog: UploadDialogService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // PEEK on entry so the hint can be shown, CONSUME on selection so a stored
    // destination is single-use — leaving it behind hijacks the next unrelated
    // navigation, which reads as the app jumping somewhere nobody asked for.
    this.returnTo = peekIntendedUrl();

    // THE RESOLVED VIEW COMES FROM THE SERVER, already merged across global,
    // company and user and already normalised. The client renders it rather than
    // re-deriving the cascade — there is one implementation of the merge order and
    // it is not this one.
    this.preferences.effective$
      .pipe(takeUntil(this.destroying$))
      .subscribe((effective) => {
        this.companyName = effective.companyName ?? null;
        this.companyLocked = (effective.lockedFields ?? []).includes('projectGridView');
        if (effective.projectGridView) {
          // MERGE, DON'T CLOBBER. A stored view keeps every explicit choice the
          // user made; the only thing that changes is that a built-in column the
          // view never knew about appears at its shipped default. Saving after
          // this records the column as known, so a later hide is explicit and
          // sticks.
          this.gridView = mergeUnknownColumns(effective.projectGridView as ProjectGridView);
          this.rebuildGrid();
        }
        this.refreshSaveTargets();
      });

    this.refreshSaveTargets();

    // The load stream is the ONE signal that the project list changed, so it is
    // where the search index and the model are rebuilt. Rebuilding anywhere else is
    // how this pane ended up recomputing during change detection instead.
    this.projectService.projectListLoad$
      .pipe(takeUntil(this.destroying$))
      .subscribe((load) => {
        this.projectLoad = load;
        this.onProjectsChanged();
      });

  }

  private refreshSaveTargets(): void {
    this.saveTargets = saveTargets(
      {
        isSuperAdmin: this.authService.hasRole('SuperAdmin'),
        isCompanyAdmin: this.authService.hasRole('administrator'),
        companyLocked: this.companyLocked,
      },
      this.companyName
    );
  }

  private get saveContext() {
    return {
      isSuperAdmin: this.authService.hasRole('SuperAdmin'),
      isCompanyAdmin: this.authService.hasRole('administrator'),
      companyLocked: this.companyLocked,
    };
  }

  /**
   * Save the CURRENT layout to one tier.
   *
   * Sends the complete view, not a delta: the tier is replaced wholesale, because a
   * partial save cannot express "I removed a column" — an absence in the payload is
   * indistinguishable from "I did not mention it".
   */
  saveView(tier: PreferenceTier): void {
    this.saveStatus = null;
    this.preferences.saveProjectGridView(tier, this.gridView as SavedProjectGridView).subscribe({
      next: () => (this.saveStatus = `Saved${tier === 'user' ? '' : ' for everyone in this ' + (tier === 'company' ? 'company' : 'product')}.`),
      // Never a silent failure: a save that vanished looks identical to one that
      // worked until the next reload contradicts it.
      error: () => (this.saveStatus = 'Could not save. Your layout is unchanged on the server.'),
    });
  }

  /** Clear a tier, so the cascade falls through to the one below it. */
  resetView(tier: PreferenceTier): void {
    this.saveStatus = null;
    this.preferences.saveProjectGridView(tier, null).subscribe({
      next: () => (this.saveStatus = 'Reset.'),
      error: () => (this.saveStatus = 'Could not reset. Your layout is unchanged on the server.'),
    });
  }

  resetLabelFor(tier: PreferenceTier): string {
    return resetLabel(tier, this.saveContext, this.companyName);
  }

  canReset(tier: PreferenceTier): boolean {
    return canResetTier(tier, this.saveContext);
  }

  readonly defaultSaveTier = DEFAULT_SAVE_TIER;

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
    // The list-freshness socket is NOT stopped here any more. It is owned by
    // MainLayoutComponent, which outlives this screen — stopping it on navigation
    // is the defect this change fixes, because /temperature is exactly where a
    // user sits while a cure goes live.
  }

  /** Called from the search box, the status chip and the date inputs. */
  onCriteriaChanged(): void {
    this.rebuildGrid();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.rebuildGrid();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.selectedFilter = 'all';
    this.dateFilterFrom = '';
    this.dateFilterTo = '';
    this.rebuildGrid();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  /** An expired session needs a sign-in, not a retry — retrying fails identically. */
  signInAgain(): void {
    this.authService.login();
  }

  /**
   * The project they wanted is not listed — go straight to the OS file picker.
   *
   * This used to ask main-layout to show a picker of its own, and nothing was
   * listening, so the click did nothing visible. A hidden input clicked
   * programmatically is the whole mechanism: the browser's own dialog already
   * filters to the accepted types, so there is no in-app pane to render, nothing
   * to keep in sync, and one dialog instead of two.
   */
  @ViewChild('projectUploadInput') projectUploadInput?: ElementRef<HTMLInputElement>;

  openUpload(): void {
    this.projectUploadInput?.nativeElement.click();
  }

  /**
   * Hand the chosen file to the upload pipeline, which lives in main-layout
   * because an upload in progress must survive a route change.
   */
  onUploadFileChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Cleared so choosing the SAME file again still fires a change event, which
    // is the most common next action after a failed upload.
    input.value = '';
    if (file) this.uploadDialog.uploadFile(file);
  }

  toggleColumnChooser(): void {
    this.showColumnChooser = !this.showColumnChooser;
  }

  // ── Grid capabilities. Every one is a pure transformation of the view, applied
  //    here and then rebuilt once. The rules live in project-grid-actions so they
  //    can be asserted without a DOM; this class only decides when to apply them.

  onSortToggle(change: { columnId: string; additive: boolean }): void {
    const column = this.columnCatalogue.find((c) => c.id === change.columnId);
    this.gridView = toggleSort(this.gridView, change.columnId, column, change.additive);
    this.rebuildGrid();
  }

  onFilterChange(change: { columnId: string; filter: ProjectGridFilter | null }): void {
    this.gridView = setFilter(this.gridView, change.columnId, change.filter);
    this.rebuildGrid();
  }

  onToggleColumn(columnId: string): void {
    this.gridView = toggleColumn(this.gridView, columnId);
    this.rebuildGrid();
  }

  onMoveColumn(columnId: string, delta: number): void {
    this.gridView = moveColumn(this.gridView, columnId, delta);
    this.rebuildGrid();
  }

  onToggleGroupBy(columnId: string): void {
    this.gridView = toggleGroupBy(this.gridView, columnId);
    this.rebuildGrid();
  }

  isColumnVisible(columnId: string): boolean {
    return this.gridView.columns.includes(columnId);
  }

  isGrouped(columnId: string): boolean {
    return this.gridView.groupBy.includes(columnId);
  }

  /** Position in the visible order, for the reorder arrows. -1 when hidden. */
  columnPosition(columnId: string): number {
    return this.gridView.columns.indexOf(columnId);
  }

  get visibleColumnCount(): number {
    return this.gridView.columns.length;
  }

  /**
   * How many columns this user could show at all.
   *
   * THE CATALOGUE, NOT THE BUILT-IN LIST. columnCatalogue is the exact array the
   * picker renders, so the number in the label and the number of rows in the menu
   * cannot disagree — which is the ambiguity "Columns (6)" had, where the count
   * could be read as either the visible ones or the available ones.
   *
   * It is rebuilt from availableColumns(projects) on every grid rebuild, so a
   * user-defined field that only appears once its projects load raises this total
   * the moment it becomes selectable, with no extra wiring.
   */
  get selectableColumnCount(): number {
    return this.columnCatalogue.length;
  }

  retryProjectLoad(): void {
    this.projectService.loadProjectList(
      this.authService.getObjectId(),
      this.authService.getCurrentCustomerTenantId()
    );
  }

  /**
   * Choosing a project resumes wherever the user was heading, or the default tab
   * when they came here on purpose. The returnTo has already been sanitised to a
   * same-origin path — see safeReturnTo, and the open-redirect it exists to prevent.
   */
  onSelectProject(project: ProjectInfo): void {
    this.projectService.setProject(project.ProjectName);
    this.router.navigateByUrl(destinationAfterSelection(consumeIntendedUrl()));
  }

  copyProjectLink(project: ProjectInfo, event: Event): void {
    event.stopPropagation();
    const name = project?.ProjectName;
    if (!name) return;

    const link = buildProjectLink(window.location.origin, '/temperature', name);

    navigator.clipboard?.writeText(link).then(
      () => {
        this.shareLinkCopiedFor = name;
        setTimeout(() => {
          if (this.shareLinkCopiedFor === name) this.shareLinkCopiedFor = null;
        }, 2000);
      },
      (err: unknown) => {
        // Clipboard access can be denied outright; say so rather than showing a
        // success tick for a copy that did not happen.
        console.warn('ProjectSelection > could not copy project link', err);
        window.prompt('Copy this link:', link);
      }
    );
  }

  private onProjectsChanged(): void {
    const projects = this.projectService.projectList ?? [];
    this.haystacks = new Map(projects.map((p) => [p, searchHaystack(p)]));
    // The catalogue is data-dependent — one column per user-defined field the served
    // projects actually carry — so it is rebuilt with the list, not per render.
    this.columnCatalogue = availableColumns(projects);
    this.rebuildGrid();
  }

  private rebuildGrid(): void {
    this.gridModel = buildProjectGridModel(
      this.projectService.projectList ?? [],
      this.gridView,
      {
        searchTerm: this.searchTerm,
        statusFilter: this.selectedFilter,
        dateFrom: this.dateFilterFrom,
        dateTo: this.dateFilterTo,
      },
      this.haystacks,
      // The grid renders dates in the user's chosen ORDER, resolved from the
      // cascade. Read at build time rather than held as state so a preference
      // change is picked up by the next rebuild without another subscription.
      normalizeDateFormat(this.preferences.effective$.value.dateFormat),
      normalizeTimeFormat(this.preferences.effective$.value.timeFormat)
    );
  }
}
